import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import isEmpty from "lodash/isEmpty";
import Form, { FORM_HORIZONTAL_LAYOUT } from "../../../components/Form";
import useFormSubmit from "../../../components/Form/useFormSubmit";
import { reloadTable } from "../../../components/SmartTable/reducer";
import { closeModalAction } from "../../../components/Modal/reducer";
import Button from "../../../components/Button";
import { renderDynamicallyFields } from "../../../components/Form/methods";
import { useModalData } from "../../../components/Modal/useModalData";
import axios from "../../../axios";
import { openConfirm } from "../../../components/Modal/Confirm";
import { getInitialValidity, hasValidityChanged, prepareTrainingDataToSend, TABLE_TRG_TRAININGS } from "./methods";
import { buildTrainingFormFields } from "./trainingFormFields";
import { MODAL_TRG_TRAINING } from "./trainingConstants";

/**
 * TrainingForm renders the modal form used to create or edit a TRG training record.
 *
 * The form is large and covers all training attributes across several logical sections:
 * identification (code, area, method, tags, branch scope, name, version, description),
 * classification (label, category, duration, language, alternative languages, prerequisites),
 * validity (validity type and its associated numeric/date sub-fields, notification period,
 * linked training), provider (internal responsible person or external provider company),
 * and misc (internal remarks, active flag for new records only).
 *
 * For edit mode the form fetches full training details on mount via a dedicated GET request
 * (including relations) because the modal data object only carries a minimal subset of fields.
 * Editing the validity configuration shows a confirmation dialog to warn the user that
 * existing qualifications linked to this training may be affected.
 *
 * Fields that belong to shared/imported trainings (shared_by_company_name is set) are
 * rendered as disabled to prevent local modifications to centrally managed content.
 *
 * @returns {React.ReactElement} The training create/edit form rendered inside an Ant Design Form.
 */
const TrainingForm = () => {
  const data = useModalData(MODAL_TRG_TRAINING);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isNewRecord = isEmpty(data);
  const [branchesVisible, setBranchesVisible] = useState(false);
  const [language, setLanguage] = useState();
  const [validity, setValidity] = useState();
  const [linkedTraining, setLinkedTraining] = useState();
  const [internalProvider, setInternalProvider] = useState();
  const [editLoading, setEditLoading] = useState(false);
  const { loading, submit, generalError } = useFormSubmit({
    form,
    route: isNewRecord ? "trg/trainings" : `trg/trainings/${data.id}`,
    method: isNewRecord ? "post" : "put",
    transformer: (values) => prepareTrainingDataToSend(values),
    onSuccess: () => {
      // Reset filters when creating a new training, so it appears at the top of the table
      dispatch(reloadTable({ name: TABLE_TRG_TRAININGS, resetFilters: isNewRecord }));
      dispatch(closeModalAction({ name: MODAL_TRG_TRAINING }));
    }
  });

  useEffect(() => {
    if (data.id) {
      setEditLoading(true);
      axios
        .get(`trg/trainings/${data.id}`, {
          params: { with: ["area", "branches", "provider", "prerequisites", "validity", "tags", "label"] }
        })
        .then(
          (res) => {
            const training = res.data.data;
            const range_of_validity = res.data.data.all_branches
              ? [{ value: t("trg.new_training.all_branches"), all_branches: true }]
              : training.branches?.map((item) => item.id) || undefined;
            const validity = getInitialValidity(training);

            const internal_provider = training.internal_provider ? 1 : 0;
            setValidity(validity);
            setInternalProvider(internal_provider);
            setLinkedTraining(training.linked_training_id);
            setLanguage(training.language);

            form.setFieldsValue({
              code: training.code,
              area_id: training.area_id,
              method_id: training.method_id,
              tags: training.tags?.map((item) => item.id) || undefined,
              range_of_validity: range_of_validity,
              name: training.name,
              version: training.version,
              description: training.description,
              category: training.category,
              duration: training.duration,
              language: training.language,
              alternative_languages: training.alternative_languages,
              prerequisites: training.prerequisites?.map((item) => item.id) || undefined,
              validity: validity,
              specific_date: training.validity?.specific_date,
              day_number: training.validity?.day_number,
              year_number: training.validity?.year_number,
              years_minus_one_day: training.validity?.years_minus_one_day,
              notification_period: training.notification_period,
              linked_training_id: training.linked_training_id,
              linked_training_notification: training.linked_training_notification,
              internal_provider: internal_provider,
              responsible_id: training.responsible_id,
              external_provider_id: training.external_provider_id,
              internal_remarks: training.internal_remarks,
              training_label_id: training.label?.id
            });
            setEditLoading(false);
          },
          () => {
            setEditLoading(false);
          }
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Intercepts form submission for edit mode to detect whether the validity configuration
   * has changed. If it has, a confirmation dialog is shown before proceeding, because
   * changing validity rules can affect the expiry dates of all existing qualifications
   * that reference this training.
   */
  const beforeSubmit = () => {
    if (isNewRecord) {
      return submit();
    }

    if (hasValidityChanged(data, form)) {
      openConfirm({
        content: (
          <div>
            <div>{t("trg.new_training.edit_on_confirm_body")}</div>
          </div>
        ),
        onOk: () => {
          return submit();
        }
      });
    } else {
      return submit();
    }
  };

  return (
    <Form
      disabled={editLoading}
      onSubmit={beforeSubmit}
      form={form}
      errors={generalError}
      loading={loading}
      initialValues={{ active: isNewRecord ? true : data?.active, training_label_id: data?.label?.id }}
      layout={FORM_HORIZONTAL_LAYOUT}
      header={
        <>
          <div className="h3">{t(isNewRecord ? "trg.new_training.form_title" : "trg.edit_training.form_title")}</div>
          <div className="mb-30 mt-10 h6 font-weight-300 text-muted">{t("container.general_words.form_subtitle")}</div>
        </>
      }
    >
      {renderDynamicallyFields(
        buildTrainingFormFields({
          data,
          t,
          form,
          isNewRecord,
          branchesVisible,
          setBranchesVisible,
          validity,
          setValidity,
          language,
          setLanguage,
          linkedTraining,
          setLinkedTraining,
          internalProvider,
          setInternalProvider
        })
      )}
      <div className="flex mt-25 flex-justify-end">
        <Button
          className="min-w-150"
          type="primary"
          htmlType="submit"
          disabled={loading}
          loading={loading}
          icon={<i className="ti-check" />}
        >
          {t("container.general_words.save")}
        </Button>
      </div>
    </Form>
  );
};

export default TrainingForm;
